import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, setup } from "xstate";

import type {
  T_WebSettingsAccountUpdate,
  T_WebSettingsPanel,
} from "./web-settings-contract";

export {
  WEB_SETTINGS_PANELS,
  type T_WebSettingsAccountUpdate,
  type T_WebSettingsPanel,
} from "./web-settings-contract";

export const WEB_SETTINGS_PANEL_TITLES = {
  account: "Manage account",
  brokerages: "Brokerages",
  brokerageConnect: "Connect brokerage",
  deleteAccount: "Delete account",
  investorProfile: "Investor profile",
  manualHoldings: "Manual holdings",
  notifications: "Notifications",
  referrals: "Referrals",
  root: "Settings",
  subscription: "Subscription",
  usage: "Usage",
} as const satisfies Record<T_WebSettingsPanel, string>;

export type T_WebSettingsEvent =
  | { type: "OPEN_SETTINGS" }
  | { type: "OPEN_INVESTOR_PROFILE" }
  | { type: "OPEN_BROKERAGES" }
  | { type: "OPEN_BROKERAGE_CONNECT" }
  | {
      accountUpdate?: T_WebSettingsAccountUpdate | null;
      type: "OPEN_ACCOUNT";
    }
  | { type: "OPEN_NOTIFICATIONS" }
  | { type: "OPEN_MANUAL_HOLDINGS" }
  | { type: "OPEN_SUBSCRIPTION" }
  | { type: "OPEN_USAGE" }
  | { referralCode?: string | null; type: "OPEN_REFERRALS" }
  | { type: "OPEN_DELETE_ACCOUNT" }
  | { type: "BACK" }
  | { type: "CLOSE_SETTINGS" };

export type T_WebSettingsContext = {
  accountUpdate: T_WebSettingsAccountUpdate | null;
  referralCode: string | null;
};

type T_WebSettingsOpenData = {
  accountUpdate?: T_WebSettingsAccountUpdate | null;
  referralCode?: string | null;
};

const webSettingsSetupTypes: SetupTypes<
  T_WebSettingsContext,
  T_WebSettingsEvent,
  Record<never, string>,
  string,
  undefined,
  unknown,
  EventObject,
  MetaObject
> = {};

/**
 * Web owns this hierarchy because its settings content and provider handoffs
 * differ from native. All Lattice-owned settings navigation remains inside
 * this machine; URL intents are only an external entry/return boundary.
 */
export const webSettingsMachine = setup({
  types: webSettingsSetupTypes,
  actions: {
    captureAccountUpdate: assign({
      accountUpdate: ({ event }) =>
        event.type === "OPEN_ACCOUNT" ? (event.accountUpdate ?? null) : null,
      referralCode: null,
    }),
    captureReferralCode: assign({
      accountUpdate: null,
      referralCode: ({ event }) =>
        event.type === "OPEN_REFERRALS" ? (event.referralCode ?? null) : null,
    }),
    clearExternalIntent: assign({
      accountUpdate: null,
      referralCode: null,
    }),
  },
}).createMachine({
  id: "webSettings",
  initial: "closed",
  context: { accountUpdate: null, referralCode: null },
  on: {
    CLOSE_SETTINGS: { actions: "clearExternalIntent", target: ".closed" },
    OPEN_ACCOUNT: {
      actions: "captureAccountUpdate",
      target: ".settings.account.root",
    },
    OPEN_BROKERAGES: {
      actions: "clearExternalIntent",
      target: ".settings.brokerages",
    },
    OPEN_BROKERAGE_CONNECT: {
      actions: "clearExternalIntent",
      target: ".settings.brokerageConnect",
    },
    OPEN_DELETE_ACCOUNT: {
      actions: "clearExternalIntent",
      target: ".settings.account.deleteAccount",
    },
    OPEN_INVESTOR_PROFILE: {
      actions: "clearExternalIntent",
      target: ".settings.investorProfile",
    },
    OPEN_MANUAL_HOLDINGS: {
      actions: "clearExternalIntent",
      target: ".settings.account.manualHoldings",
    },
    OPEN_NOTIFICATIONS: {
      actions: "clearExternalIntent",
      target: ".settings.account.notifications",
    },
    OPEN_REFERRALS: {
      actions: "captureReferralCode",
      target: ".settings.account.referrals",
    },
    OPEN_SETTINGS: {
      actions: "clearExternalIntent",
      target: ".settings.root",
    },
    OPEN_SUBSCRIPTION: {
      actions: "clearExternalIntent",
      target: ".settings.account.subscription",
    },
    OPEN_USAGE: {
      actions: "clearExternalIntent",
      target: ".settings.account.usage",
    },
  },
  states: {
    closed: {},
    settings: {
      initial: "root",
      states: {
        root: {
          on: {
            BACK: "#webSettings.closed",
            OPEN_ACCOUNT: {
              actions: "captureAccountUpdate",
              target: "account.root",
            },
            OPEN_BROKERAGES: {
              actions: "clearExternalIntent",
              target: "brokerages",
            },
            OPEN_BROKERAGE_CONNECT: {
              actions: "clearExternalIntent",
              target: "brokerageConnect",
            },
            OPEN_INVESTOR_PROFILE: {
              actions: "clearExternalIntent",
              target: "investorProfile",
            },
          },
        },
        investorProfile: { on: { BACK: "root" } },
        brokerages: { on: { BACK: "root" } },
        brokerageConnect: { on: { BACK: "root" } },
        account: {
          initial: "root",
          states: {
            root: {
              on: {
                BACK: "#webSettings.settings.root",
                OPEN_DELETE_ACCOUNT: {
                  actions: "clearExternalIntent",
                  target: "deleteAccount",
                },
                OPEN_MANUAL_HOLDINGS: {
                  actions: "clearExternalIntent",
                  target: "manualHoldings",
                },
                OPEN_NOTIFICATIONS: {
                  actions: "clearExternalIntent",
                  target: "notifications",
                },
                OPEN_REFERRALS: {
                  actions: "captureReferralCode",
                  target: "referrals",
                },
                OPEN_SUBSCRIPTION: {
                  actions: "clearExternalIntent",
                  target: "subscription",
                },
                OPEN_USAGE: {
                  actions: "clearExternalIntent",
                  target: "usage",
                },
              },
            },
            notifications: { on: { BACK: "root" } },
            manualHoldings: { on: { BACK: "root" } },
            subscription: { on: { BACK: "root" } },
            usage: { on: { BACK: "root" } },
            referrals: { on: { BACK: "root" } },
            deleteAccount: { on: { BACK: "root" } },
          },
        },
      },
    },
  },
});

export function getWebSettingsOpenEvent(
  panel: T_WebSettingsPanel,
  data: T_WebSettingsOpenData = {},
): T_WebSettingsEvent {
  switch (panel) {
    case "root":
      return { type: "OPEN_SETTINGS" };
    case "investorProfile":
      return { type: "OPEN_INVESTOR_PROFILE" };
    case "brokerages":
      return { type: "OPEN_BROKERAGES" };
    case "brokerageConnect":
      return { type: "OPEN_BROKERAGE_CONNECT" };
    case "account":
      return { accountUpdate: data.accountUpdate, type: "OPEN_ACCOUNT" };
    case "notifications":
      return { type: "OPEN_NOTIFICATIONS" };
    case "manualHoldings":
      return { type: "OPEN_MANUAL_HOLDINGS" };
    case "subscription":
      return { type: "OPEN_SUBSCRIPTION" };
    case "usage":
      return { type: "OPEN_USAGE" };
    case "referrals":
      return { referralCode: data.referralCode, type: "OPEN_REFERRALS" };
    case "deleteAccount":
      return { type: "OPEN_DELETE_ACCOUNT" };
  }
}

export function getWebSettingsPanel(value: unknown): T_WebSettingsPanel {
  if (typeof value !== "object" || value === null || !("settings" in value)) {
    return "root";
  }

  const settings = value.settings;
  if (
    settings === "investorProfile" ||
    settings === "brokerages" ||
    settings === "brokerageConnect"
  ) {
    return settings;
  }
  if (
    typeof settings !== "object" ||
    settings === null ||
    !("account" in settings)
  ) {
    return "root";
  }

  const account = settings.account;
  if (
    account === "notifications" ||
    account === "manualHoldings" ||
    account === "subscription" ||
    account === "usage" ||
    account === "referrals" ||
    account === "deleteAccount"
  ) {
    return account;
  }
  return "account";
}
